import {Component, Inject, OnDestroy, OnInit, ViewChild, ChangeDetectionStrategy} from "@angular/core";
import {MAT_DIALOG_DATA, MatDialogRef} from "@angular/material/dialog";
import {NumericEditorComponent} from "@irb-common/components/grid/editors/numeric-editor.component";
import {IDropdownItem} from "@irb-common/models/dropdown.model";
import {UtilityService} from "@irb-common/services/utility.service";
import {IrbEditorsService} from "@irb/irb-editors/irb-editors.service";
import {CalltoactionService} from "@irb-common/components/calltoaction/calltoaction.service";
import {DeleteHeaderComponent} from "@irb-common/components/grid/renderers/delete.header.component";
import {DeleteCellComponent} from "@irb-common/components/grid/renderers/delete.cell.component";
import {NumberTextBoxEditorComponent} from "@irb-common/components/grid/editors/number-text-box-editor.component";
import {NumberTextBoxRendererComponent} from "@irb-common/components/grid/renderers/number-text-box.cell.component";
import {IrbEditorsEvents} from "@irb/irb-editors/irb-editors.events";
import {AppService} from "@irb/app.service";
import {IrbAppEvents} from "@irb/app.events";
import {IAlertDialogData} from "@irb/app.model";
import {IrbModelsEvents} from '@irb/irb-models/irb-models.events';
import {Subscription} from 'rxjs';
import {IEvent} from '@irb-common/events';

@Component({ standalone: false,
  selector: "irb-editor-buy-multiple-replacement",
  templateUrl: "./editor-buy-multiple-replacement.component.html",
  styleUrls: ["./editor-buy-multiple-replacement.component.scss", "../../irb-common/styles/dialog.scss"],
  changeDetection: ChangeDetectionStrategy.Eager
})
export class EditorBuyMultipleReplacementComponent implements OnInit, OnDestroy {
  numberReplacements: number = 0;
  totalAllocationValue: number;
  gridOptions: any;
  private grid: any;
  replacements: Array<any> = [];
  @ViewChild("securitiesDropdown") securitiesDropdown: any;
  securitiesList: any[];
  isReadOnly: boolean;
  private eventSubscription: Subscription;
  addNewSecurityItem: any = {
    button: 'Add new security',
    type: 'security',
    fromComp: 'EditorBuyMultipleReplacementComponent'
  };
  isUiTest = false;
  frameworkComponents: any;

  constructor(public dialogRef: MatDialogRef<EditorBuyMultipleReplacementComponent>, @Inject(MAT_DIALOG_DATA) public mdd: any, private util: UtilityService, 
  private ies: IrbEditorsService, private ctas: CalltoactionService, 
  private as: AppService) {
    if (mdd && mdd.replacements && mdd.replacements.length !== 0) {
      this.numberReplacements = mdd.replacements.length;
      this.replacements = this.util.clone(mdd.replacements);
    }

    this.gridOptions = {
      columnDefs: mdd.isReadOnly ? this.getDisabledColumnDefs() : this.getColumnDefs(),      
      context: { componentParent: this },
      floatingFilter: false,
      onCellValueChanged: () => this.cellValueChanged(),
      overlayNoRowsTemplate: "<span class='ag-overlay-loadGriding-center ag-overlay-message'>No Securities for replacement</span>",
      isExternalFilterPresent: () => true,
      doesExternalFilterPass: this.doesExternalFilterPass.bind(this)
    };
    
    this.frameworkComponents = this.getFrameworkComponents();
  }

  ngOnInit(): void {
    this.isReadOnly = this.mdd.isReadOnly;
    this.eventSubscription = this.as.getEvents().subscribe((s) => {
      this.processEvent(s);
    });
  }

  ngOnDestroy(): void {
    if (this.eventSubscription) {
      this.eventSubscription.unsubscribe();
    }
  }

  private processEvent(event: IEvent) {
    switch (event.type) {
      case IrbModelsEvents.SAVE_CREATE_NEW_SECURITY:
        if (event.data) {
          if (event.data.type === 'security' && event.data.fromComp === 'EditorBuyMultipleReplacementComponent') {
            this.resetSecuritySearch(event.data)
          }
        }
        break;
    }
  }

  onGridReady(params: any) {
    this.grid = params;
    this.grid.api.setGridOption('rowData', this.replacements);
    this.cellValueChanged();
  }

  doesExternalFilterPass(node: any) {
    return !node.data.isUIdeleted;
  }

  close() {
    this.as.fireEvent(IrbEditorsEvents.UPDATE_FROM_BUY_MULTIPLE_REPLACEMENT, {
      data: this.mdd.data,
      replacements: this.mdd.replacements,
    });
    
    this.dialogRef.close();
  }

  done() {
    let errMsg: string = "";
    
    if (this.numberReplacements > 0 && this.numberReplacements < 2) {
      errMsg = "Please add two or more securities, which must total 100%.";
    } else if (this.numberReplacements > 0 && this.totalAllocationValue !== 100) {
      errMsg = "Total allocation percentage should be equal to 100%";
    } else {
      const r = this.replacements.filter((item: any) => !item.isUIdeleted);

      for (let i = 0; i < r.length; i++) {
        if (r[i].allocationPercent === 0) {
          errMsg = "Allocation% cannot be 0%";
          break;
        }
      }

      if (errMsg === "") {
        const dt: any = {
          data: this.mdd.data,
          replacements: this.util.clone(this.replacements),
        };
        
        this.as.fireEvent(IrbEditorsEvents.UPDATE_FROM_BUY_MULTIPLE_REPLACEMENT, dt);
        this.dialogRef.close();
        return;
      } 
    }

    this.as.fireEvent(IrbAppEvents.SHOW_ALERT, {
      dTitle: "Alert",
      msg: errMsg,
      dProceedBtnLabel: "Close",
      dType: "ERROR",
    } as IAlertDialogData);
  }

  cellValueChanged() {
    this.updateTotalAllocationValue();
    this.numberReplacements = this.replacements.filter((item: any) => !item.isUIdeleted).length;
  }


  getSecurities(searchStr: any) {
    if (!searchStr || (searchStr && searchStr.trim() === "")) {
      this.securitiesDropdown.set([]);
      return;
    }
    const sortByKey = this.util.sortByKey;
    this.ies.getSecuritiesSearchDropDown(searchStr).subscribe((data: any) => {
      sortByKey(data.items, "securitySymbol", "asc");
      this.securitiesDropdown.set(data.items);
    });
  }

  onSecuritiesChange(s: Array<IDropdownItem>) {
    this.securitiesList = s;
  }

  addSecuritiesFromDropdown() {
    let gridData: Array<any> = [], newSec: any;
    let duplicate: any = [];

    this.replacements.forEach((existingSec: any) => {
      this.securitiesList.forEach((sec: any) => {
        if (existingSec.securityId === sec.value) {
          duplicate.push(sec.value);
        }
      });
    });

    if (duplicate.length > 0) {
      this.as.fireEvent(IrbAppEvents.SHOW_TOAST, {
        severity: "warn",
        summary: "",
        detail: "Security(ies) cannot be listed twice.",
      });
    }

    duplicate.forEach((dup: any) => {
      this.securitiesList = this.securitiesList.filter((x: any) => {
        return x.value !== dup;
      });
    });

    if (this.mdd && this.mdd.data) {
      const originalSymbolId = this.mdd.data.securityId;
      const existOriginal = this.securitiesList.filter((x: any) => {
        return x.value === originalSymbolId;
      });

      if (existOriginal && existOriginal.length !== 0) {
        this.as.fireEvent(IrbAppEvents.SHOW_TOAST, {
          severity: "warn",
          summary: "",
          detail: "Original security cannot be listed.",
        });

        this.securitiesList = this.securitiesList.filter((x: any) => {
          return x.value !== originalSymbolId;
        });
      }
    }

    this.securitiesList.forEach((sec: any) => {
      newSec = {
        securitySymb: sec.name,
        securityId: sec.value,
        allocationPercent: 0.00,
      };
      gridData.push(newSec);
    });

    this.replacements = this.replacements.concat(gridData);
    this.numberReplacements = this.replacements.filter((item: any) => !item.isUIdeleted).length;
    this.grid.api.setGridOption('rowData', this.replacements);
    this.securitiesDropdown.clear();
  }

  delete(cc: any) {
    for (let i = 0; i < this.replacements.length; i++) {
      if (this.replacements[i].securitySymb === cc.securitySymb) {
        if(this.replacements[i].isNew) {
          this.replacements.splice(i--, 1);
        } else {
          this.replacements[i].isUIdeleted = true;
        }
      }
    }
    
    this.grid.api.setGridOption('rowData', this.replacements);
    this.cellValueChanged();
  }

  deleteAll() {
    this.replacements.forEach((item: any) => { item.isUIdeleted = true; });
    this.numberReplacements = 0;
    this.updateTotalAllocationValue();
    this.grid.api.deselectAll();
    this.grid.api.hideOverlay();
    this.grid.api.setGridOption('rowData', this.replacements);
  }

  updateTotalAllocationValue() {
    this.totalAllocationValue = this.replacements.filter((item: any) => !item.isUIdeleted).reduce((a, b) => a + (b["allocationPercent"] || 0), 0);
  }

  cancelDelete() {
    this.ctas.remove();
  }

  getRowCount() {
    return this.replacements.filter((item: any) => !item.isUIdeleted).length;
  }

  private getFrameworkComponents() {
    return {
      numericEditorComponent: NumericEditorComponent,
      DeleteHeaderComponent: DeleteHeaderComponent,
      DeleteCellComponent: DeleteCellComponent,
      NumberTextBoxEditorComponent: NumberTextBoxEditorComponent,
      "NumberTextBoxRendererComponent": NumberTextBoxRendererComponent
    };
  }

  isGridEditable() {
    return this.isReadOnly;
  }

  private getColumnDefs() {
    const columns: any = [
      {
        headerName: "Delete All",
        headerComponent: "DeleteHeaderComponent",
        headerComponentParams: {
          message: "Remove all items?",
          note: "",
          proceed: this.deleteAll.bind(this),
          cancel: this.cancelDelete.bind(this),
        },
        cellRenderer: "DeleteCellComponent",
        cellRendererParams: {
          message: "Remove this item?",
          note: "",
          proceed: this.delete.bind(this),
          cancel: this.cancelDelete.bind(this),
        },
        resizable: false,
        minWidth: 25,
        maxWidth: 25,
        width: 25,
        pinned: "left",
        suppressColumnsToolPanel: true,
        lockPosition: true,
      },
      {
        headerName: "Replace with",
        children: [
          {
            headerName: "Symbol",
            field: "securitySymb",
            sort: "asc",
            width: 150,
            cellRenderer: "cellRendererFormatter",
          },
          {
            headerName: "Allocation%",
            field: "allocationPercent",
            width: 150,
            /*editable: true,
            cellClass: 'numberTextBoxEditor',
            cellEditor: "NumberTextBoxEditorComponent",
            cellEditorParams: () => {
              return {
                min: 0,
                places: 2,
                invalidRangeMessage: "Value can not be negative.",
                autofocus: true,
                autoSelect: true
              };
            },
            cellRenderer: 'cellRendererFormatter',
            cellRendererParams: () => ({
                type: 'currency',
                decimalPosition: '2',
                displaySymbol: '%'
            }),
            cellStyle: { "text-align": "right" },*/
            cellClass: "numberTextBoxCell",
            cellRenderer: "NumberTextBoxRendererComponent",
            cellRendererParams: () => {
              return {
                min: 0,
                max: 100,
                places: 2,
                isPercentage: true,
                onChange: (params: any, c: any) => {
                  if (params.data.allocationPercent != c.value && c.isValid) {
                    params.data.allocationPercent = parseFloat(c.value);
                    params.api.redrawRows({ rowNodes: [params.node] });
                    this.cellValueChanged();
                  }
                },
              };
            },
          },
        ],
      },
    ];

    return columns;
  }

  private getDisabledColumnDefs() {
    const columns: any = [
      {
        headerName: "Delete All",
        headerComponent: "DeleteHeaderComponent",
        headerComponentParams: {
          message: "Remove all items?",
          note: "",
          proceed: this.deleteAll.bind(this),
          cancel: this.cancelDelete.bind(this),
          disableDelete: this.isGridEditable.bind(this),
        },
        cellRenderer: "DeleteCellComponent",
        cellRendererParams: {
          message: "Remove this item?",
          note: "",
          proceed: this.delete.bind(this),
          cancel: this.cancelDelete.bind(this),
          disableDelete: this.isGridEditable,
        },
        resizable: false,
        minWidth: 25,
        maxWidth: 25,
        width: 25,
        pinned: "left",
        suppressColumnsToolPanel: true,
        lockPosition: true,
      },
      {
        headerName: "Replace with",
        children: [
          {
            headerName: "Symbol",
            field: "securitySymb",
            sort: "asc",
            width: 150,
            cellRenderer: "cellRendererFormatter",
          },
          {
            headerName: "Allocation%",
            field: "allocationPercent",
            width: 150,
            cellRenderer: 'cellRendererFormatter',
            cellRendererParams: () => ({
                type: 'currency',
                decimalPosition: '2',
                displaySymbol: '%'
            }),
            editable: false,
            cellStyle: { "text-align": "right" },
          },
        ],
      },
    ];

    return columns;
  }

  openNewItemSecurityFlow(dataParam: any) {
    dataParam = Object.assign({}, this.addNewSecurityItem, dataParam);
    this.as.fireEvent(IrbModelsEvents.SHOW_CREATE_SECURITIES, dataParam);
  }

  resetSecuritySearch(dataParam: any) {
    if (dataParam && dataParam.newItemsForDD && dataParam.newItemsForDD.length !== 0) {
      this.securitiesList = dataParam.newItemsForDD;
      this.securitiesDropdown.setValue(dataParam.newItemsForDD);
    }
  }
}
